import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { Target, AlertTriangle, TrendingDown, Clock } from 'lucide-react';
import type { MonteCarloResult } from '@/hooks/useEmpMonteCarlo';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Props {
  mc: MonteCarloResult;
}

const fmtDate = (d: string) => {
  try { return format(new Date(d), 'dd/MM/yy', { locale: ptBR }); } catch { return d; }
};

const probColor = (p: number) => {
  if (p >= 70) return 'text-primary';
  if (p >= 40) return 'text-warning';
  return 'text-destructive';
};

const probBadge = (p: number): 'default' | 'secondary' | 'destructive' => {
  if (p >= 70) return 'default';
  if (p >= 40) return 'secondary';
  return 'destructive';
};

const sevIcon = (s: string) => {
  if (s === 'alto' || s === 'critico') return <AlertTriangle className="h-3 w-3 text-destructive" />;
  if (s === 'medio') return <TrendingDown className="h-3 w-3 text-warning" />;
  return <Clock className="h-3 w-3 text-muted-foreground" />;
};

export const McProbCard: React.FC<Props> = ({ mc }) => {
  const drivers = Array.isArray(mc.drivers_risco) ? mc.drivers_risco : [];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Probability of on-time completion */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-2">
            <Target className="h-4 w-4" />
            <span>Probabilidade de Cumprimento</span>
          </div>
          <p className={`text-3xl font-bold ${probColor(mc.prob_on_time)}`}>
            {mc.prob_on_time.toFixed(0)}%
          </p>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="mt-3 space-y-1 text-xs text-muted-foreground cursor-help">
                  <div className="flex justify-between">
                    <span>P10 (otimista)</span>
                    <span className="font-medium text-foreground">{fmtDate(mc.p10_data)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>P50 (mediana)</span>
                    <span className="font-medium text-foreground">{fmtDate(mc.p50_data)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>P80</span>
                    <span className="font-medium text-foreground">{fmtDate(mc.p80_data)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>P90 (conservador)</span>
                    <span className="font-medium text-foreground">{fmtDate(mc.p90_data)}</span>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <p className="text-xs">
                  Baseado em {mc.n_iteracoes.toLocaleString()} simulações Monte Carlo.
                  Média: {mc.media_dias.toFixed(0)} dias · σ: {mc.desvio_padrao_dias.toFixed(0)} dias
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <div className="mt-2">
            <Badge variant={probBadge(mc.prob_on_time)}>
              {mc.prob_on_time >= 70 ? 'Provável no prazo' :
               mc.prob_on_time >= 40 ? 'Risco moderado' : 'Alto risco de atraso'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Risk drivers */}
      {drivers.length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-2">
              <AlertTriangle className="h-4 w-4" />
              <span>Fatores de Risco</span>
            </div>
            <div className="space-y-2">
              {drivers.slice(0, 4).map((d, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  {sevIcon(d.severidade)}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground leading-tight">{d.fator}</p>
                    <p className="text-xs text-muted-foreground">{d.impacto}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
